import { UserState } from '@/composition/users'
import { FileOpener } from '@capacitor-community/file-opener'
import { Browser } from '@capacitor/browser'
import { Capacitor } from '@capacitor/core'
import { Directory, Filesystem } from '@capacitor/filesystem'
import moment from 'moment-timezone'

export enum CreateEventActions {
  Date = 'date',
  Color = 'color',
  StartTime = 'startTime',
  EndTime = 'endTime',
  Timezone = 'timezone',
  Location = 'location',
  EventVisibility = 'eventVisibility',
  Level = 'LEVEL',
  Course = 'COURSE',
  PrivateChannel = 'CHANNEL',
  RecurringEndDate = 'recurringEndDate',
  RepeatOccurence = 'repeatOccurence',
  RepeatsMonthly = 'repeatsMonthly',
}

export const disablePastDates = (
  date: Date | number | string,
  _disablePastDate?: Date,
  _buffer = 86400000,
  timezone: string = moment.tz.guess()
) => {
  // Get the current time in the target timezone
  const now = moment().tz(timezone)

  // For date picker, we're only concerned with the date portion (not time)
  // When checking if a date should be disabled
  const nowDate = now.clone().startOf('day')

  // Convert the input date to a moment object in the target timezone
  let dateToCheck

  if (date instanceof Date) {
    // If it's a JavaScript Date object (which is always in local timezone)
    // Extract the date components and create a new date in the target timezone
    dateToCheck = moment
      .tz(timezone)
      .year(date.getFullYear())
      .month(date.getMonth())
      .date(date.getDate())
      .startOf('day')
  } else if (typeof date === 'number') {
    // If it's a timestamp, convert to a Date first then extract components
    const tempDate = new Date(date)
    dateToCheck = moment
      .tz(timezone)
      .year(tempDate.getFullYear())
      .month(tempDate.getMonth())
      .date(tempDate.getDate())
      .startOf('day')
  } else {
    // For string or other formats
    dateToCheck = moment.tz(date, timezone).startOf('day')
  }

  // Disable if the date is before today in the target timezone
  return dateToCheck.isBefore(nowDate)
}

export const flattenFormData = (data: any) => {
  return {
    title: data.titleAndColor.title,
    color: data.titleAndColor.color,
    startTime: data.dateAndTime.startTime,
    endTime: data.dateAndTime.endTime,
    location: data.location.location,
    locationUrl: data.location.locationUrl,
    description: data.location.description,
    hideLocation: data.location.hideLocation,
    isRecurring: data.recurring.isRecurring,
    repeatOccurence: data.recurring.repeatOccurence,
    repeatInterval: data.recurring.repeatInterval,
    repeatsOn: data.recurring.repeatsOn,
    recurringEndDate: data.recurring.recurringEndDate,
    isNeverEnds: data.recurring.isNeverEnds,
    monthlyRepeatType: data.recurring.monthlyRepeatType,
    afterOccurrences: data.recurring.afterOccurrences,
    attendees: data.visibility.attendees,
    levels: data.visibility.levels,
    course: data.visibility.course,
    privateChannel: data.visibility.privateChannel,
    remind: data.visibility.remind,
    hideAttendees: data.visibility.hideAttendees,
    eventType: data.payment.eventType,
    eventPrice: data.payment.eventPrice,
    isTestMode: data.payment.isTestMode,
    currency: data.payment.currency,
    eventCoverUrl: data.cover.eventCoverUrl,
    timezone: data.timezone,
  }
}

export const unflattenFormData = (flatData: any) => {
  return {
    titleAndColor: {
      title: flatData.title,
      color: flatData.color,
    },
    dateAndTime: {
      startTime: flatData.startTime,
      endTime: flatData.endTime,
    },
    location: {
      location: flatData.location,
      locationUrl: flatData.locationUrl,
      description: flatData.description,
      hideLocation: flatData.hideLocation,
    },
    recurring: {
      isRecurring: flatData.isRecurring,
      repeatOccurence: flatData.repeatOccurence,
      repeatInterval: flatData.repeatInterval,
      repeatsOn: flatData.repeatsOn,
      recurringEndDate: flatData.recurringEndDate,
      isNeverEnds: flatData.isNeverEnds,
      monthlyRepeatType: flatData.monthlyRepeatType,
      afterOccurrences: flatData.afterOccurrences,
    },
    visibility: {
      attendees: flatData.attendees,
      levels: flatData.levels,
      course: flatData.course,
      privateChannel: flatData.privateChannel,
      remind: flatData.remind,
      hideAttendees: flatData.hideAttendees,
    },
    payment: {
      eventType: flatData.eventType,
      eventPrice: flatData.eventPrice,
      currency: flatData.currency,
      isTestMode: flatData.isTestMode,
    },
    cover: {
      eventCoverUrl: flatData.eventCoverUrl,
    },
    timezone: flatData.timezone,
    hideAttendees: flatData.hideAttendees,
  }
}

interface AddToCalendarOptions {
  eventId: string
  calendarType: 'google' | 'ical'
  getCalendarData: (eventId: string, calendarType: string) => Promise<any>
}

export const calendarOptions = [
  { label: 'Google', value: 'google' },
  { label: 'Apple', value: 'apple' },
  { label: 'Yahoo', value: 'yahoo' },
  { label: 'Outlook', value: 'outlook' },
]

export const addToCalendar = async ({
  eventId,
  calendarType,
  getCalendarData,
}: AddToCalendarOptions) => {
  try {
    const response = await getCalendarData(eventId, calendarType)
    /**
     * Android
     *  - Download ical in cache and open with file opener
     *
     */
    if (Capacitor.getPlatform() === 'android') {
      const blob = await fetch(response).then(res => res.blob())
      await downloadAndOpenIcs(blob, eventId)
      return
    }

    /**
     *
     * IOS
     *  - Google
     *    - Use AppLauncher to open the url
     *  - Apple and other calendar apps
     *    - Open ics file url in external browser
     *
     * UPDATE: The above is not working as expected, so we are just opening the url in the browser
     *
     * REASON: Google calendar app in iOS has a limitation where it does not respect the recur parameter in the url,
     *         so event a valid recurring event is added to the calendar as a single event
     *
     */
    if (Capacitor.getPlatform() === 'ios') {
      await Browser.open({
        url: response,
      })
      return
    }

    // If not native platform and google calendar
    if (calendarType === 'google') {
      window.open(response, '_blank')
      return
    }

    // if not native platform and not google calendar, download the file from gloud storage url
    const blob = await fetch(response).then(res => res.blob())
    const fileName = `event-${eventId}.ics`
    triggerDownload(blob, fileName)
  } catch (error: any) {
    throw error
  }
}

const downloadAndOpenIcs = async (blob: Blob, eventId: string) => {
  const reader = new FileReader()
  reader.readAsDataURL(blob)
  reader.onloadend = async function () {
    const base64data = reader.result as string
    if (base64data) {
      const sysFile = await Filesystem.writeFile({
        path: `${eventId}.ics`,
        directory: Directory.Cache,
        data: base64data,
      })
      await FileOpener.open({
        filePath: sysFile.uri,
      })
    }
  }
}

const triggerDownload = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', fileName)
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

export const roundToNext30Minutes = date => {
  const userTimezone =
    UserState.state.currentUser?.timeZone || moment.tz.guess()
  const m = moment(date).tz(userTimezone)
  const minutes = m.minutes()
  const roundedMinutes = Math.ceil(minutes / 30) * 30
  return m.minutes(roundedMinutes).seconds(0).milliseconds(0)
}

export const isTimeDisabled = (
  startTime: Date,
  current: number,
  isStartDate: boolean,
  timezone: string
) => {
  const currentMoment = moment().tz(timezone)
  const selectedMoment = moment(current).tz(timezone)
  const startMoment = moment(startTime).tz(timezone)

  const isHourDisabled = (hour: number) => {
    if (isStartDate) {
      // If it's the current date, disable hours before current hour
      if (selectedMoment.isSame(currentMoment, 'day')) {
        return hour < currentMoment.hour()
      }
      return false
    } else {
      // For end time, disable hours before start time on same day
      if (selectedMoment.isSame(startMoment, 'day')) {
        return hour < startMoment.hour()
      }
      return false
    }
  }

  const isMinuteDisabled = (minute: number, hour: number) => {
    if (isStartDate) {
      // If it's current hour on current day, disable minutes before current minute
      if (
        selectedMoment.isSame(currentMoment, 'day') &&
        hour === currentMoment.hour()
      ) {
        return minute < currentMoment.minute()
      }
      return false
    } else {
      // For end time, disable minutes before start time in same hour
      if (
        selectedMoment.isSame(startMoment, 'day') &&
        hour === startMoment.hour()
      ) {
        return minute < startMoment.minute()
      }
      return false
    }
  }

  return { isHourDisabled, isMinuteDisabled }
}

export function formatEventTimes(startTime, endTime) {
  const userTimezone =
    UserState.state.currentUser?.timeZone || moment.tz.guess()

  // Convert to moment objects in user's timezone
  const startMoment = moment(startTime).tz(userTimezone)
  const endMoment = moment(endTime).tz(userTimezone)

  // Handle case where end time is before start time
  if (endMoment.isBefore(startMoment)) {
    endMoment.date(startMoment.date())
  }

  // Format the date using moment to ensure timezone is respected
  const formattedDate = startMoment.format('Do MMM YYYY')

  // Format times
  const fromTime = startMoment.format('h:mm A')
  const toTime = endMoment.format('h:mm A')

  return {
    date: formattedDate,
    fromTime: fromTime,
    toTime: toTime,
  }
}

export enum VisibilityType {
  PUBLIC = 'PUBLIC',
  PRIVATE = 'PRIVATE',
  CHANNEL = 'CHANNEL',
  COURSE = 'COURSE',
  LEVEL = 'LEVEL',
}

export enum RSVPStatus {
  ATTENDING = 'ATTENDING',
  DECLINED = 'DECLINED',
}

export enum RecurringEndType {
  ON = 'on',
  AFTER = 'after',
  NEVER = 'never',
}

export enum PaymentStatus {
  PAID = 'PAID',
  PENDING = 'PENDING',
  FAILED = 'FAILED',
}

interface ExportOptions {
  eventId: string
  getExportData: (eventId: string) => Promise<string>
}

export const handleAttendeesCSVExport = async ({
  eventId,
  getExportData,
}: ExportOptions) => {
  try {
    const response = await getExportData(eventId)
    const fileName = `event-attendees-${eventId}.csv`

    if (
      Capacitor.getPlatform() === 'ios' ||
      Capacitor.getPlatform() === 'android'
    ) {
      console.log('CSV FILE response :: ', response)
      await Browser.open({
        url: response,
      })
      return
    }

    // Web - Download file directly
    const blob = await fetch(response).then(res => res.blob())
    triggerDownload(blob, fileName)
  } catch (error: any) {
    throw error
  }
}

export function formatEventTimeAndDate(startTime, endTime, event) {
  const localTimezone =
    UserState.state.currentUser?.timeZone || moment.tz.guess()
  const eventTimezone = event?.timezone || localTimezone

  // For recurring events, we need to maintain the original local time in the event's timezone
  // This is how Google Calendar works - recurring events maintain their original local time
  if (event?.recurringMeta?.rrule) {
    const startMoment = moment.utc(startTime)
    const endMoment = moment.utc(endTime)

    // Get timezone object
    const zone = moment.tz.zone(eventTimezone)
    if (!zone) return { timeString: '', dateString: '', month: '', date: '' }

    // Determine offset based on when the recurring event series starts
    const eventStartDate = event?.recurringMeta?.eventStart

    try {
      // Use the offset from when the recurring series started
      const referenceDate = moment.utc(eventStartDate || startTime)
      // Ensure the reference date is valid
      if (!referenceDate.isValid()) {
        throw new Error('Invalid reference date')
      }

      const offset = zone.utcOffset(referenceDate.valueOf())
      const offsetHours = Math.floor(Math.abs(offset) / 60)
      const offsetMinutes = Math.abs(offset) % 60

      // If offset is negative (west of UTC), add hours; if positive (east of UTC), subtract hours
      const adjustHours = offset < 0 ? offsetHours : -offsetHours
      const adjustMinutes = offset < 0 ? offsetMinutes : -offsetMinutes

      // Convert to event timezone time by adjusting hours and minutes
      const eventStartMoment = startMoment
        .clone()
        .add(adjustHours, 'hours')
        .add(adjustMinutes, 'minutes')
        .tz(eventTimezone, true)
      const eventEndMoment = endMoment
        .clone()
        .add(adjustHours, 'hours')
        .add(adjustMinutes, 'minutes')
        .tz(eventTimezone, true)

      // Ensure the resulting moments are valid
      if (!eventStartMoment.isValid() || !eventEndMoment.isValid()) {
        throw new Error('Invalid resulting time')
      }

      // Now convert to user's timezone for display
      const localStartTime = eventStartMoment
        .clone()
        .tz(localTimezone)
        .format('hh:mm A')
      const localEndTime = eventEndMoment
        .clone()
        .tz(localTimezone)
        .format('hh:mm A')
      const localDate = eventStartMoment
        .clone()
        .tz(localTimezone)
        .format('MM/DD/YYYY')
      const localTimeZone = moment.tz(localTimezone).zoneAbbr()

      const timeString = `${localStartTime} - ${localEndTime}`
      const dateString = `${localDate}, ${localTimeZone}`

      const month = eventStartMoment.clone().tz(localTimezone).format('MMM')
      const date = eventStartMoment.clone().tz(localTimezone).format('DD')

      return { timeString, dateString, month, date }
    } catch (error) {
      console.error('Error formatting event time:', error)
      // Fallback to a safe default using standard time
      const standardOffset = zone.utcOffset(
        moment.utc([startMoment.year(), 0, 1]).valueOf()
      )
      const offsetHours = Math.floor(Math.abs(standardOffset) / 60)
      const adjustHours = standardOffset < 0 ? offsetHours : -offsetHours

      const eventStartMoment = startMoment.clone().add(adjustHours, 'hours')
      const eventEndMoment = endMoment.clone().add(adjustHours, 'hours')

      const localStartTime = eventStartMoment
        .clone()
        .tz(localTimezone)
        .format('hh:mm A')
      const localEndTime = eventEndMoment
        .clone()
        .tz(localTimezone)
        .format('hh:mm A')
      const localDate = eventStartMoment
        .clone()
        .tz(localTimezone)
        .format('MM/DD/YYYY')
      const localTimeZone = moment.tz(localTimezone).zoneAbbr()

      const timeString = `${localStartTime} - ${localEndTime}`
      const dateString = `${localDate}, ${localTimeZone}`

      const month = eventStartMoment.clone().tz(localTimezone).format('MMM')
      const date = eventStartMoment.clone().tz(localTimezone).format('DD')

      return { timeString, dateString, month, date }
    }
  } else {
    // For non-recurring events: Convert UTC to local timezone with DST
    const localStartTime = moment
      .utc(startTime)
      .tz(localTimezone)
      .format('hh:mm A')
    const localEndTime = moment.utc(endTime).tz(localTimezone).format('hh:mm A')
    const localDate = moment
      .utc(startTime)
      .tz(localTimezone)
      .format('MM/DD/YYYY')
    const localTimeZone = moment.tz(localTimezone).zoneAbbr()

    const timeString = `${localStartTime} - ${localEndTime}`
    const dateString = `${localDate}, ${localTimeZone}`

    const month = moment.utc(startTime).tz(localTimezone).format('MMM')
    const date = moment.utc(startTime).tz(localTimezone).format('DD')

    return { timeString, dateString, month, date }
  }
}
